import { CircularProgress } from '@mui/material';
import { InboxViewModel } from './useInbox.viewModel';
import { MessagingSidebar } from './MessagingSidebar';
import {
  useCreateChatClient,
  Chat,
  ChatView,
  Channel,
  ChannelHeader,
  MessageInput,
  MessageList,
  Thread,
  Window,
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
  MessageActions,
} from 'stream-chat-react';
import type { MessageActionsProps } from 'stream-chat-react';
import { chatFormatDate } from './helpers';
import 'stream-chat-react/dist/css/v2/index.css';
import './chat.css';
import { useChatViewModel } from './useChat.viewModel';
import { useEffect, useCallback } from 'react';

const CustomUnreadMessagesSeparator = () => (
  <div className="str-chat__unread-messages-separator">New</div>
);

export const InboxChat = (p: InboxViewModel) => {
  const chatClient = useCreateChatClient({
    apiKey: import.meta.env.VITE_STREAM_CHAT_ID,
    userData: { id: p.userId },
    tokenOrProvider: p.streamToken,
  });

  if (!chatClient) {
    return (
      <div className="w-full h-full justify-center items-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="p-2.5 bg-white rounded-[10px] w-full h-full">
      <Chat client={chatClient}>
        <ChatContent userId={p.userId} />
      </Chat>
    </div>
  );
};

const ChatContent = ({ userId }: { userId: string }) => {
  const { channelListOptions } = useChatViewModel({ userId });
  return (
    <ChatView>
      <ChatView.Channels>
        <MessagingSidebar channelListOptions={channelListOptions} />
        <Channel
          MessageActions={CustomMessageActions}
          UnreadMessagesSeparator={CustomUnreadMessagesSeparator}
        >
          <ChannelContent />
          <Thread />
        </Channel>
      </ChatView.Channels>
    </ChatView>
  );
};

const ChannelContent = () => {
  const { jumpToFirstUnreadMessage } = useChannelActionContext();
  const { channel } = useChannelStateContext();

  useEffect(() => {
    if (channel) {
      jumpToFirstUnreadMessage();
    }
  }, [channel, channel?.cid, jumpToFirstUnreadMessage]);

  return (
    <Window>
      <ChannelHeader />
      <MessageList
        messageActions={['flag', 'mute', 'react', 'markUnread']}
        formatDate={chatFormatDate}
      />
      <MessageInput />
    </Window>
  );
};

/**
 * Custom MessageActions component that filters out 'markUnread' action
 * for the user's own messages.
 */
const CustomMessageActions = (props: MessageActionsProps) => {
  const { message } = useMessageContext();
  const { client } = useChatContext();

  const isOwnMessage = message?.user?.id === client.userID;

  // Override getMessageActions to filter out markUnread for own messages
  const getMessageActions = useCallback(() => {
    const baseActions: Array<'flag' | 'mute' | 'react' | 'markUnread'> = [
      'flag',
      'mute',
      'react',
    ];
    if (!isOwnMessage) {
      baseActions.push('markUnread');
    }
    return baseActions;
  }, [isOwnMessage]);

  return <MessageActions {...props} getMessageActions={getMessageActions} />;
};
